import { sentenceCase } from 'change-case';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { ThreeDots } from 'react-loader-spinner';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  TablePagination,
  Grid,
  CardHeader,
  CardContent,
  Paper,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
// components
import Label from '../../../components/label';
import Scrollbar from '../../../components/scrollbar';
import { UserListHead } from '../../../sections/@dashboard/user';
import { Page } from '../../../components';
import { fDateTime } from '../../../utils/formatTime';
import { getAssetTransferHistory } from '../store/assetActions';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'date', label: 'Date', alignRight: false },
  { id: 'location', label: 'Location', alignRight: false },
  { id: 'department', label: 'Department', alignRight: false },
  { id: 'custodian_name', label: 'Custodian', alignRight: false },
  { id: 'transferred_by', label: 'Transferred By', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
];
const renderChange = (from, to) => {
  if (!to && !from) return '—';
  if (!from || from === to) return to;
  return `${from} → ${to}`;
};
// ----------------------------------------------------------------------
AssetTransferHistory.propTypes = {
  assetId: PropTypes.string,
};

export default function AssetTransferHistory({ assetId }) {
  const dispatch = useDispatch();
  const { isLoading, assetTransferHistory, transferHistoryCount } = useSelector((state) => state.assetReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    dispatch(getAssetTransferHistory({ id: assetId, page: page + 1, rowsPerPage }));
  }, [dispatch, assetId, page, rowsPerPage]);

  return (
    <Page title="Asset Transfer History">
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12} md={12} lg={12}>
            <CardHeader title={'Transfer History'} subheader={'Movement trail for this asset'} />
            <CardContent>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                  {isLoading ? (
                    <Grid container sx={{ justifyContent: 'center' }}>
                      <ThreeDots
                        height="80"
                        width="80"
                        radius="9"
                        color="#2065d1"
                        ariaLabel="three-dots-loading"
                        wrapperStyle={{}}
                        wrapperClassName=""
                        visible
                      />
                    </Grid>
                  ) : (
                    <Table size="small">
                      <UserListHead headLabel={TABLE_HEAD} rowCount={assetTransferHistory?.length} />
                      <TableBody>
                        {assetTransferHistory?.length > 0 ? (
                          assetTransferHistory.map((record) => (
                            <TableRow hover key={record?.id} tabIndex={-1} role="checkbox">
                              <TableCell align="left">{fDateTime(record?.date)}</TableCell>
                              <TableCell align="left">{renderChange(record?.from_location, record?.to_location)}</TableCell>
                              <TableCell align="left">
                                {renderChange(record?.from_department, record?.to_department)}
                              </TableCell>
                              <TableCell align="left">
                                {renderChange(record?.from_custodian_name, record?.to_custodian_name)}
                              </TableCell>
                              <TableCell align="left">{record?.transferred_by || '—'}</TableCell>
                              <TableCell align="left">
                                <Label
                                  color={
                                    (record?.status === 'PENDING' && 'warning') ||
                                    (record?.status === 'REJECTED' && 'error') ||
                                    'success'
                                  }
                                >
                                  {record?.status ? sentenceCase(record.status) : '—'}
                                </Label>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <>Asset transfer history will appear here</>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>
              </Scrollbar>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={transferHistoryCount || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardContent>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
